import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Header from "../components/Common/Header";
import { MediaImage } from "../components/Media/MediaImage";
import {
  ConnectedPersonDetail as ConnectedPersonDetailType,
  ConnectedPersonCreate,
  getConnectedPerson,
  updateConnectedPerson,
  deleteConnectedPerson,
  deletePersonLink,
  getConnectedPersonDisplayName,
  getRelationshipTypeLabel,
} from "../api/connectedPersons";
import { getConnectedPersonMedia, MediaItem } from "../api/media";
import { 
  LocationSummary, 
  getLocationsForConnectedPerson,
  tagItemToLocation,
  untagItemFromLocation,
} from "../api/locations";
import { LocationPicker } from "../components/Location";

export default function ConnectedPersonDetail() {
  const { personId } = useParams<{ personId: string }>();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editForm, setEditForm] = useState<ConnectedPersonCreate>({});
  const [showAddLocation, setShowAddLocation] = useState(false);

  // Fetch person details
  const { data: person, isLoading, error } = useQuery<ConnectedPersonDetailType>({
    queryKey: ["connectedPerson", personId],
    queryFn: async () => {
      if (!personId) throw new Error("No person ID");
      const token = await getAccessTokenSilently();
      return getConnectedPerson(parseInt(personId), token);
    },
    enabled: !!personId,
  });

  // Fetch media for this person
  const { data: media } = useQuery<MediaItem[]>({
    queryKey: ["connectedPersonMedia", personId],
    queryFn: async () => {
      if (!personId) return [];
      const token = await getAccessTokenSilently();
      return getConnectedPersonMedia(parseInt(personId), token);
    },
    enabled: !!personId,
  });

  // Fetch locations for this person
  const { data: locations } = useQuery<LocationSummary[]>({
    queryKey: ["connectedPersonLocations", personId],
    queryFn: async () => {
      if (!personId) return [];
      const token = await getAccessTokenSilently();
      return getLocationsForConnectedPerson(token, parseInt(personId));
    },
    enabled: !!personId,
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: ConnectedPersonCreate) => {
      if (!personId) throw new Error("No person ID");
      const token = await getAccessTokenSilently();
      return updateConnectedPerson(parseInt(personId), data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connectedPerson", personId] });
      setIsEditing(false);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!personId) throw new Error("No person ID");
      const token = await getAccessTokenSilently();
      return deleteConnectedPerson(parseInt(personId), token);
    },
    onSuccess: () => {
      navigate("/connected-persons");
    },
  });

  // Delete link mutation
  const deleteLinkMutation = useMutation({
    mutationFn: async (linkId: number) => {
      if (!personId) throw new Error("No person ID");
      const token = await getAccessTokenSilently();
      return deletePersonLink(parseInt(personId), linkId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connectedPerson", personId] });
    },
  });

  // Tag location mutation
  const tagLocationMutation = useMutation({
    mutationFn: async (locationId: number) => {
      if (!personId) throw new Error("No person ID");
      const token = await getAccessTokenSilently();
      return tagItemToLocation(token, locationId, { connected_person_id: parseInt(personId) });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connectedPersonLocations", personId] });
      setShowAddLocation(false);
    },
  });

  // Untag location mutation
  const untagLocationMutation = useMutation({
    mutationFn: async (locationId: number) => {
      if (!personId) throw new Error("No person ID");
      const token = await getAccessTokenSilently();
      return untagItemFromLocation(token, locationId, { connected_person_id: parseInt(personId) });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connectedPersonLocations", personId] });
    },
  });

  const startEditing = () => {
    if (person) {
      setEditForm({
        given_name: person.given_name || "",
        surname: person.surname || "",
        sex: person.sex || "",
        birth_date_text: person.birth_date_text || "",
        birth_place_text: person.birth_place_text || "",
        death_date_text: person.death_date_text || "",
        death_place_text: person.death_place_text || "",
        notes: person.notes || "",
      });
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    updateMutation.mutate(editForm);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-vault-50">
        <Header />
        <main className="mx-auto max-w-4xl px-4 py-8">
          <div className="flex justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-vault-300 border-t-vault-600" />
          </div>
        </main>
      </div>
    );
  }

  if (error || !person) {
    return (
      <div className="min-h-screen bg-vault-50">
        <Header />
        <main className="mx-auto max-w-4xl px-4 py-8">
          <div className="rounded-lg bg-red-50 p-8 text-center">
            <h2 className="text-lg font-medium text-red-700">Person not found</h2>
            <Link to="/connected-persons" className="mt-2 text-red-600 underline">
              Back to Connected People
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vault-50">
      <Header />

      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-4">
          <Link to="/connected-persons" className="text-sm text-vault-600 hover:text-vault-900">
            ← Back to Connected People
          </Link>
        </nav>

        {/* Header Card */}
        <div className="rounded-lg bg-white p-6 shadow-sm mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className={`h-16 w-16 rounded-full flex items-center justify-center text-2xl ${
                person.sex === "M" 
                  ? "bg-blue-100 text-blue-700"
                  : person.sex === "F"
                  ? "bg-pink-100 text-pink-700"
                  : "bg-amber-100 text-amber-700"
              }`}>
                {person.sex === "M" ? "♂" : person.sex === "F" ? "♀" : "⚭"}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-serif text-2xl font-bold text-vault-900">
                    {getConnectedPersonDisplayName(person)}
                  </h1>
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
                    Connected Person
                  </span>
                </div>
                <p className="text-vault-600">
                  {person.birth_date_text && `b. ${person.birth_date_text}`}
                  {person.birth_date_text && person.birth_place_text && ` in ${person.birth_place_text}`}
                  {person.death_date_text && ` · d. ${person.death_date_text}`}
                  {person.death_date_text && person.death_place_text && ` in ${person.death_place_text}`}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={startEditing}
                className="rounded-lg bg-vault-100 px-4 py-2 text-vault-700 hover:bg-vault-200"
              >
                Edit
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="rounded-lg bg-red-100 px-4 py-2 text-red-700 hover:bg-red-200"
              >
                Delete
              </button>
            </div>
          </div>

          {person.notes && (
            <div className="mt-4 rounded-lg bg-vault-50 p-4">
              <h3 className="text-sm font-medium text-vault-700 mb-1">Notes</h3>
              <p className="text-vault-600 whitespace-pre-wrap">{person.notes}</p>
            </div>
          )}
        </div>

        {/* Connections to Family Members */}
        <div className="rounded-lg bg-white p-6 shadow-sm mb-6">
          <h2 className="font-serif text-xl font-semibold text-vault-900 mb-4">
            Connections to Family Members
          </h2>

          {person.person_links.length === 0 ? (
            <p className="text-vault-500 text-center py-4">
              No connections to family members yet.
            </p>
          ) : (
            <div className="space-y-3">
              {person.person_links.map((link) => (
                <div
                  key={link.id}
                  className="flex items-center justify-between rounded-lg border border-vault-200 p-3"
                >
                  <div>
                    <Link
                      to={`/trees/${link.tree_id}/persons/${link.person_id}`}
                      className="font-medium text-vault-900 hover:text-vault-600"
                    >
                      {link.person_name}
                    </Link>
                    <p className="text-sm text-vault-500">
                      {getRelationshipTypeLabel(link.relationship_type)}
                      {link.relationship_description && ` · ${link.relationship_description}`}
                      <span className="ml-2 text-vault-400">({link.tree_name})</span>
                    </p>
                  </div>
                  <button
                    onClick={() => deleteLinkMutation.mutate(link.id)}
                    className="text-vault-400 hover:text-red-600"
                    title="Remove connection"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Locations */}
        <div className="rounded-lg bg-white p-6 shadow-sm mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-xl font-semibold text-vault-900">
              Locations ({locations?.length || 0})
            </h2>
            <button
              onClick={() => setShowAddLocation(!showAddLocation)}
              className="text-sm text-vault-600 hover:text-vault-900 flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Location
            </button>
          </div>

          {/* Add Location Picker */}
          {showAddLocation && (
            <div className="mb-4 p-4 bg-vault-50 rounded-lg">
              <LocationPicker
                value={null}
                onChange={(id) => {
                  if (id) {
                    tagLocationMutation.mutate(id);
                  }
                }}
                placeholder="Search for a location to add..."
              />
              <button
                onClick={() => setShowAddLocation(false)}
                className="mt-2 text-sm text-vault-600 hover:text-vault-900"
              >
                Cancel
              </button>
            </div>
          )}

          {!locations || locations.length === 0 ? (
            <p className="text-vault-500 text-center py-4">
              No locations tagged yet.
            </p>
          ) : (
            <div className="space-y-2">
              {locations.map((loc) => (
                <div
                  key={loc.id}
                  className="flex items-center justify-between py-2 border-b border-vault-100 last:border-0"
                >
                  <Link
                    to={`/locations/${loc.id}`}
                    className="flex items-center gap-2 text-vault-900 hover:text-vault-600"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div>
                      <span className="font-medium">{loc.name}</span>
                      {(loc.city || loc.country) && (
                        <span className="text-sm text-vault-500 ml-2">
                          ({[loc.city, loc.country].filter(Boolean).join(", ")})
                        </span>
                      )}
                    </div>
                  </Link>
                  <button
                    onClick={() => untagLocationMutation.mutate(loc.id)}
                    className="text-vault-400 hover:text-red-600 text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Photos */}
        {media && media.length > 0 && (
          <div className="rounded-lg bg-white p-6 shadow-sm">
            <h2 className="font-serif text-xl font-semibold text-vault-900 mb-4">
              Photos ({media.length})
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {media.map((item) => (
                <div key={item.id} className="aspect-square rounded-lg overflow-hidden bg-vault-100">
                  <MediaImage
                    mediaId={item.id}
                    alt={item.original_filename}
                    className="h-full w-full object-cover"
                    cacheBuster={item.file_size}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Edit Modal */}
      {isEditing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <h2 className="mb-4 text-xl font-semibold text-vault-900">
              Edit Connected Person
            </h2>

            {updateMutation.error && (
              <div className="mb-4 rounded bg-red-50 p-3 text-sm text-red-700">
                {updateMutation.error instanceof Error ? updateMutation.error.message : "Failed to update"}
              </div>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-vault-700">First Name</label>
                  <input
                    type="text"
                    className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                    value={editForm.given_name}
                    onChange={(e) => setEditForm({ ...editForm, given_name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-vault-700">Last Name</label>
                  <input
                    type="text"
                    className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                    value={editForm.surname}
                    onChange={(e) => setEditForm({ ...editForm, surname: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-vault-700">Sex</label>
                <select
                  className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                  value={editForm.sex}
                  onChange={(e) => setEditForm({ ...editForm, sex: e.target.value })}
                >
                  <option value="">Unknown</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-vault-700">Birth Date</label>
                  <input
                    type="text"
                    className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                    value={editForm.birth_date_text}
                    onChange={(e) => setEditForm({ ...editForm, birth_date_text: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-vault-700">Birth Place</label>
                  <input
                    type="text"
                    className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                    value={editForm.birth_place_text}
                    onChange={(e) => setEditForm({ ...editForm, birth_place_text: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-vault-700">Death Date</label>
                  <input
                    type="text"
                    className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                    value={editForm.death_date_text}
                    onChange={(e) => setEditForm({ ...editForm, death_date_text: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-vault-700">Death Place</label>
                  <input
                    type="text"
                    className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                    value={editForm.death_place_text}
                    onChange={(e) => setEditForm({ ...editForm, death_place_text: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-vault-700">Notes</label>
                <textarea
                  className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                  rows={4}
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setIsEditing(false)}
                className="rounded-lg bg-vault-100 px-4 py-2 text-vault-700 hover:bg-vault-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={updateMutation.isPending}
                className="rounded-lg bg-vault-600 px-4 py-2 text-white hover:bg-vault-700 disabled:opacity-50"
              >
                {updateMutation.isPending ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-2 text-lg font-semibold text-vault-900">
              Delete Connected Person?
            </h2>
            <p className="mb-4 text-vault-600">
              Are you sure you want to delete{" "}
              <span className="font-medium">{getConnectedPersonDisplayName(person)}</span>?
              This will also remove all their connections and photo tags.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="rounded-lg bg-vault-100 px-4 py-2 text-vault-700 hover:bg-vault-200"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="rounded-lg bg-red-600 px-4 py-2 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
